import _isEmpty from 'lodash/isEmpty';

import { RB_DATA, RB_DATA_FIELD, RB_DEFAULT_KEY } from '../info';

import { ResumeBuilderSchema, STEP_INDICES } from './schema';

export const getFilledAndUnFilledSteps = (currentRBState = {}) => {
  const allSteps = [
    ...Object.values(currentRBState).slice(STEP_INDICES.CONTACT, STEP_INDICES.WORK_HISTORY + 1),
    ...Object.values(currentRBState).slice(STEP_INDICES.EDUCATION, STEP_INDICES.FINISH),
  ].map(({ index, navLabel, data }) => ({ index, navLabel, data }));

  const interestsStepIndex = allSteps.findIndex((step) => step.index === STEP_INDICES.INTERESTS);

  // First Delete Interests Step
  if (interestsStepIndex !== -1) {
    allSteps.splice(interestsStepIndex, 1);
  }

  let unfilledSteps = allSteps.filter((step) => _isEmpty(step.data));

  let filledSteps = allSteps.filter((step) => !_isEmpty(step.data));

  const certData = currentRBState[STEP_INDICES.INTERESTS].data[RB_DATA.EXTRA_CERTIFICATIONS] || {};

  const languageData = currentRBState[STEP_INDICES.INTERESTS].data[RB_DATA.EXTRA_LANGUAGES] || {};

  if (_isEmpty(certData)) {
    unfilledSteps.push({
      index: STEP_INDICES.INTERESTS,
      subIndex: 1,
      navLabel: 'Certifications',
    });
  } else {
    filledSteps.push({
      index: STEP_INDICES.INTERESTS,
      subIndex: 1,
      navLabel: 'Certifications',
    });
  }

  if (_isEmpty(languageData)) {
    unfilledSteps.push({
      index: STEP_INDICES.INTERESTS,
      subIndex: 2,
      navLabel: 'Languages',
    });
  } else {
    filledSteps.push({
      index: STEP_INDICES.INTERESTS,
      subIndex: 2,
      navLabel: 'Languages',
    });
  }

  const customSections = Object.values(
    currentRBState[STEP_INDICES.INTERESTS]?.data?.customSections || {}
  ).map((customSection, idx) => ({
    index: STEP_INDICES.INTERESTS,
    subIndex: 2 + idx,
    navLabel: customSection.sectionName,
    value: customSection.details,
    imgName: 'CustomSection',
  }));

  filledSteps.push(...customSections.filter((section) => section.value));

  unfilledSteps.push(...customSections.filter((section) => !section.value));

  return { filledSteps, unfilledSteps };
};

export const getRequiredFields = (currentRBState, theme) => {
  let requiredFields = {};
  const contactDetails =
    currentRBState[STEP_INDICES.CONTACT].data[RB_DATA.HEADER]?.[RB_DEFAULT_KEY.HEADER] || {};
  const firstName = contactDetails[RB_DATA_FIELD.HEADER.FIRST_NAME] || '';
  const lastName = contactDetails[RB_DATA_FIELD.HEADER.LAST_NAME] || '';
  const email = contactDetails[RB_DATA_FIELD.HEADER.EMAIL] || '';
  if (firstName && lastName && email) {
    requiredFields = Object.assign(requiredFields, {
      firstName,
      lastName,
      email,
      resumeTemplate: theme || localStorage.getItem('rb-color-theme') || 'ColorTheme1',
    });
  }
  return requiredFields;
};

export const getRBFlow = ({
  currentStepIndex = 0,
  currentSubStepIndex = 0,
  currentRBState = ResumeBuilderSchema,
  user = { isLoggedIn: false },
}) => {
  // Next Redux state for resume builder flow
  let nextStepIndex = currentStepIndex;
  let nextSubStepIndex = currentSubStepIndex;
  let nextBtnLabel = 'Next';
  let errorFields = {};

  // Conditional Navigation Flow Variables
  const isUserSignedIn = !!(user && user.isLoggedIn);

  const interestsStepSubSteps = currentRBState[STEP_INDICES.INTERESTS].subSteps || {};

  const interestsStepSubStepsLength = Object.keys(interestsStepSubSteps).length;

  const interestsStepData =
    currentRBState[STEP_INDICES.INTERESTS]?.data?.[RB_DATA.EXTRA]?.[RB_DEFAULT_KEY.EXTRA_DEFAULT] ||
    {};

  const certificationsSelected = interestsStepData[RB_DATA_FIELD.EXTRA.INCLUDE_CERTIFICATIONS];

  const languagesSelected = interestsStepData[RB_DATA_FIELD.EXTRA.INCLUDE_LANGUAGES];

  const customSections = currentRBState[STEP_INDICES.INTERESTS]?.data?.[RB_DATA.EXTRA_CUSTOM] || {};

  const oneOrMoreEmployementHistory = !!Object.values(
    currentRBState[STEP_INDICES.WORK_HISTORY].data?.[RB_DATA.WORK_HISTORY] || {}
  ).length;

  const educationalDetails = Object.values(
    currentRBState[STEP_INDICES.EDUCATION].data?.[RB_DATA.EDUCATION] || {}
  );

  const oneOrMoreEducationHistory = !!educationalDetails?.length;

  const hasSkills = !_isEmpty(currentRBState[STEP_INDICES.SKILLS].data?.[RB_DATA.SKILLS] || {});

  const degreeNotHighSchool = educationalDetails.some((eduDetail) => {
    const degreeValue = eduDetail[RB_DATA_FIELD.EDUCATION.DEGREE] || '';
    return degreeValue && degreeValue !== 'None' && degreeValue !== 'High School';
  });

  const getNextBtnLabel = (label = '') => `Next${label ? `: ${label}` : ''}`;

  const onNext = () => {
    switch (currentStepIndex) {
      case STEP_INDICES.LANDING:
        switch (currentSubStepIndex) {
          case 0:
            nextStepIndex += 2;
            nextSubStepIndex = 0;
            nextBtnLabel = getNextBtnLabel(currentRBState[nextStepIndex + 2]?.btnLabel);
            break;
          default:
            nextSubStepIndex = 1;
            break;
        }
        break;
      case STEP_INDICES.CONTACT:
        nextStepIndex += 1;
        nextSubStepIndex = 0;
        nextBtnLabel = 'Next: Work History';
        break;
      case STEP_INDICES.SUMMARY:
        if (oneOrMoreEmployementHistory) {
          nextSubStepIndex = 2;
        } else {
          nextSubStepIndex = 0;
        }
        nextStepIndex += 1;
        if (isUserSignedIn && oneOrMoreEmployementHistory) {
          nextBtnLabel = 'Next: Education';
        }
        break;
      case STEP_INDICES.WORK_HISTORY:
        switch (currentSubStepIndex) {
          case 2:
            if (isUserSignedIn) {
              nextStepIndex += 2;
              if (oneOrMoreEducationHistory) {
                nextSubStepIndex = 2;
                nextBtnLabel = 'Next: Skills';
              } else {
                nextSubStepIndex = 0;
              }
            } else {
              nextStepIndex += 1;
              nextSubStepIndex = 0;
            }
            break;
          case 1:
            nextSubStepIndex += 1;
            nextBtnLabel = 'Next: Education';
            break;
          default:
            nextSubStepIndex += 1;
        }
        break;
      case STEP_INDICES.SIGNUP:
        if (isUserSignedIn) {
          if (oneOrMoreEducationHistory) {
            nextSubStepIndex = 2;
          } else {
            nextSubStepIndex = 0;
          }
          nextStepIndex += 1;
        }
        break;
      case STEP_INDICES.EDUCATION:
        switch (currentSubStepIndex) {
          case 0:
            if (degreeNotHighSchool) {
              nextSubStepIndex += 1;
            } else {
              if (hasSkills) {
                nextSubStepIndex = 1;
              } else {
                nextSubStepIndex = 0;
              }
              nextBtnLabel = 'Next: Interests';
              nextStepIndex += 1;
            }
            break;
          case 2:
            if (hasSkills) {
              nextSubStepIndex = 1;
              nextBtnLabel = 'Next: Interests';
            } else {
              nextSubStepIndex = 0;
            }
            nextStepIndex += 1;
            break;
          case 1:
            nextSubStepIndex += 1;
            nextBtnLabel = 'Next: Skills';
            break;
          default:
            nextSubStepIndex += 1;
        }
        break;
      case STEP_INDICES.SKILLS:
        nextSubStepIndex = 0;
        nextStepIndex += 1;
        nextBtnLabel = 'Next: Finish';
        break;
      case STEP_INDICES.INTERESTS:
        switch (currentSubStepIndex) {
          case 0: {
            if (certificationsSelected) {
              nextSubStepIndex += 1;
            } else if (languagesSelected) {
              nextSubStepIndex += 2;
              nextBtnLabel = getNextBtnLabel(currentRBState[nextStepIndex + 1]?.btnLabel);
            } else if (interestsStepSubStepsLength > 3) {
              nextSubStepIndex = 3;
            } else {
              nextSubStepIndex = 0;
              nextStepIndex += 1;
            }

            const customSectionsErrors = Object.values(customSections).reduce((acc, item) => {
              if (item.sectionName.trim() !== '') return acc;

              return [
                ...acc,
                {
                  sectionId: item.sectionId,
                  error: 'Please enter a section name to proceed.',
                },
              ];
            }, []);

            if (interestsStepSubStepsLength > 3 && customSectionsErrors.length) {
              errorFields['customSection'] = customSectionsErrors;
              nextSubStepIndex = 0;
            }
            break;
          }
          case 1:
            if (languagesSelected) {
              nextSubStepIndex += 1;
              nextBtnLabel = getNextBtnLabel(
                interestsStepSubStepsLength > 3 ? interestsStepSubSteps[3]?.title : 'Finish'
              );
            } else if (interestsStepSubStepsLength > 3) {
              nextSubStepIndex = 3;
              nextBtnLabel = getNextBtnLabel(
                interestsStepSubSteps[nextSubStepIndex + 1]?.title || 'Finish'
              );
            } else {
              nextSubStepIndex = 0;
              nextStepIndex += 1;
            }
            break;
          case 2:
            if (interestsStepSubStepsLength > 3) {
              nextSubStepIndex += 1;
            } else {
              nextSubStepIndex = 0;
              nextStepIndex += 1;
            }
            break;
          default:
            if (currentSubStepIndex > 2 && currentSubStepIndex < interestsStepSubStepsLength - 1) {
              nextSubStepIndex += 1;
              nextBtnLabel = getNextBtnLabel(
                interestsStepSubSteps[nextSubStepIndex + 1]?.title || 'Finish'
              );
            } else {
              nextSubStepIndex = 0;
              nextStepIndex += 1;
            }
        }
        break;
      default:
        nextStepIndex += 1;
        nextSubStepIndex = 0;
        nextBtnLabel = getNextBtnLabel(currentRBState[nextStepIndex + 1]?.btnLabel);
    }

    // Will return next step index and next substep index
    return {
      nextStepIndex,
      nextSubStepIndex,
      nextBtnLabel,
      errorFields,
    };
  };

  const onPrev = () => {
    switch (currentStepIndex) {
      case STEP_INDICES.TEMPLATE_GALLERY:
        nextStepIndex -= 2;
        nextSubStepIndex = 1;
        nextBtnLabel = getNextBtnLabel(currentRBState[nextStepIndex + 2]?.btnLabel);
        break;
      case STEP_INDICES.CONTACT:
        nextStepIndex -= 1;
        nextSubStepIndex = 0;
        nextBtnLabel = 'Next: Contact';
        break;
      case STEP_INDICES.SUMMARY:
        nextStepIndex -= 1;
        nextSubStepIndex = 0;
        nextBtnLabel = 'Next: Summary';
        break;
      case STEP_INDICES.WORK_HISTORY:
        switch (currentSubStepIndex) {
          case 0:
          case 2:
            nextStepIndex -= 1;
            nextSubStepIndex = 0;
            nextBtnLabel = 'Next: Work History';
            break;
          default:
            nextStepIndex -= 1;
            nextSubStepIndex -= 1;
            nextBtnLabel = 'Next';
            break;
        }
        break;
      case STEP_INDICES.SIGNUP:
        if (oneOrMoreEmployementHistory) {
          nextSubStepIndex = 2;
          nextBtnLabel = 'Next: Education';
        } else {
          nextSubStepIndex = 0;
          nextBtnLabel = 'Next';
        }
        nextStepIndex -= 1;
        break;
      case STEP_INDICES.EDUCATION:
        switch (currentSubStepIndex) {
          case 1:
            nextSubStepIndex -= 1;
            nextBtnLabel = 'Next';
            break;
          default:
            nextSubStepIndex = oneOrMoreEmployementHistory ? 2 : 0;
            nextStepIndex -= isUserSignedIn ? 2 : 1;
            nextBtnLabel = 'Next: Education';
            break;
        }
        break;
      case STEP_INDICES.SKILLS:
        nextStepIndex -= 1;
        nextSubStepIndex = oneOrMoreEducationHistory ? 2 : 0;
        break;
      case STEP_INDICES.INTERESTS:
        switch (currentSubStepIndex) {
          case 0:
            nextSubStepIndex = 1;
            nextStepIndex -= 1;
            break;
          case 1:
            nextSubStepIndex -= 1;
            break;
          case 2:
            if (certificationsSelected) {
              nextSubStepIndex -= 1;
            } else {
              nextSubStepIndex -= 2;
            }
            break;
          default:
            if (currentSubStepIndex > 3) {
              nextSubStepIndex -= 1;
            } else if (languagesSelected) {
              nextSubStepIndex = 2;
            } else if (certificationsSelected) {
              nextSubStepIndex = 1;
            } else {
              nextSubStepIndex = 0;
            }
        }
        break;
      default:
        nextStepIndex -= 1;
        nextSubStepIndex = 0;
        nextBtnLabel = getNextBtnLabel(currentRBState[nextStepIndex + 1]?.btnLabel);
    }

    // Will return next step index and next substep index
    return {
      nextStepIndex,
      nextSubStepIndex,
      nextBtnLabel,
    };
  };

  const onTargetStep = (targetStepIndex, targetSubStepIndex = 0) => {
    let nextStepIndex = targetStepIndex;
    let nextSubStepIndex = targetSubStepIndex;
    let nextBtnLabel = 'Next';
    let nextToNextStep = currentRBState[nextStepIndex + 1];

    switch (targetStepIndex) {
      case STEP_INDICES.CONTACT:
        nextBtnLabel = 'Next: Summary';
        break;
      case STEP_INDICES.SUMMARY:
        nextBtnLabel = `Next: Work History`;
        break;
      case STEP_INDICES.WORK_HISTORY:
        if (!isUserSignedIn) {
          nextBtnLabel = 'Next: Education';
        } else if (oneOrMoreEmployementHistory) {
          nextBtnLabel = 'Next: Education';
          nextSubStepIndex = 2;
        }
        break;
      case STEP_INDICES.SIGNUP:
        if (isUserSignedIn) {
          nextStepIndex += 1;
        }
        break;
      case STEP_INDICES.EDUCATION:
        if (oneOrMoreEducationHistory) {
          nextSubStepIndex = 2;
          nextBtnLabel = 'Next: Skills';
        } else {
          nextBtnLabel = 'Next';
        }
        break;
      case STEP_INDICES.SKILLS:
        nextBtnLabel = `Next: Interests`;
        break;
      case STEP_INDICES.INTERESTS:
        switch (targetSubStepIndex) {
          case 2:
            nextBtnLabel = `Next${
              nextStepIndex >= 0 && !!nextToNextStep ? `: ${nextToNextStep.btnLabel}` : ''
            }`;
            break;
        }
    }

    return {
      nextStepIndex,
      nextSubStepIndex,
      nextBtnLabel,
    };
  };

  const navigateStepByStepUrl = (stepUrl) => {
    let stepIndex = 0;
    let subStepIndex = 0;
    const steps = Object.values(currentRBState);
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      const subSteps = Object.values(step.subSteps);
      if (subSteps.some((subStep) => subStep.url === stepUrl)) {
        subStepIndex = subSteps.findIndex((subStep) => subStep.url === stepUrl);
        stepIndex = step.index;
        break;
      }
    }
    return onTargetStep(stepIndex, subStepIndex);
  };

  return {
    onNext,
    onPrev,
    onTargetStep,
    navigateStepByStepUrl,
  };
};
